import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { TrendingUp } from 'lucide-react';
import './HomeTicker.css';

// Cache keys and duration
const TICKER_CACHE_KEY = 'wassla_home_ticker_v1';
const TICKER_CACHE_DURATION = 30 * 60 * 1000; // 30 minutes

// Fallback items - shown immediately while API loads
const FALLBACK_ITEMS = [
  { kind: 'CATEGORY', labelAr: '🔥 الأكثر مشاهدة: سيارات', labelEn: '🔥 Most viewed: Cars', target: '/?category=vehicles' },
  { kind: 'CATEGORY', labelAr: '🔥 الأكثر مشاهدة: إلكترونيات', labelEn: '🔥 Most viewed: Electronics', target: '/?category=electronics' },
  { kind: 'CATEGORY', labelAr: '🔥 الأكثر مشاهدة: عقارات', labelEn: '🔥 Most viewed: Real Estate', target: '/?category=real-estate' },
  { kind: 'SEARCH', labelAr: '🔎 الأكثر بحثًا: آيفون', labelEn: '🔎 Top search: iPhone', target: '/?search=iphone' },
  { kind: 'NEW', labelAr: '🆕 جديد اليوم: إعلانات جديدة', labelEn: '🆕 New today: Fresh ads', target: '/?category=all' },
];

// Animation speed (pixels per second)
const SPEED_PX_PER_SEC = 55;

/**
 * HomeTicker - Ultra-stable JS ticker using requestAnimationFrame
 * - Starts moving instantly (fallback items render immediately)
 * - RTL + LTR supported with proper direction
 * - No React re-render per frame (DOM style mutated directly)
 * - Clickable items navigate correctly with HashRouter
 */
function HomeTicker() {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const isArabic = i18n.language?.startsWith('ar');
  const isRTL = isArabic;
  
  // Start with fallback items immediately - swap when API returns
  const [items, setItems] = useState(FALLBACK_ITEMS);
  
  // Refs for animation
  const viewportRef = useRef(null);
  const trackRef = useRef(null);
  const contentRef = useRef(null);
  const rafRef = useRef(null);
  
  // Mutable animation state (no re-renders)
  const xRef = useRef(0);
  const lastTsRef = useRef(0);
  const contentWRef = useRef(0);
  const pausedRef = useRef(false);
  
  // Navigation ref to avoid stale closure
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  
  // Fetch ticker data with caching
  useEffect(() => {
    let cancelled = false;
    
    const fetchData = async () => {
      try {
        // Check cache first
        const cached = localStorage.getItem(TICKER_CACHE_KEY);
        if (cached) {
          try {
            const { data, timestamp } = JSON.parse(cached);
            const age = Date.now() - timestamp;
            
            if (age < TICKER_CACHE_DURATION && data?.items?.length > 0) {
              if (!cancelled) setItems(data.items);
              return;
            }
          } catch (e) {
            // Invalid cache, continue to fetch
          }
        }
        
        // Fetch fresh data
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/home/ticker`
        );
        
        if (response.ok) {
          const data = await response.json();
          
          if (!cancelled && data.items && data.items.length > 0) {
            setItems(data.items);
            
            // Cache the data
            localStorage.setItem(TICKER_CACHE_KEY, JSON.stringify({
              data,
              timestamp: Date.now()
            }));
          }
        }
      } catch (err) {
        // Keep fallback items on error
        console.error('Ticker fetch error:', err);
      }
    };
    
    fetchData();
    
    // Refresh every 10 minutes
    const interval = setInterval(fetchData, 10 * 60 * 1000);
    
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);
  
  // Display items (limit to 20)
  const displayItems = useMemo(() => items.slice(0, 20), [items]);
  
  // Track ticker click (fire and forget)
  const trackClick = useCallback((item) => {
    try {
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/analytics/event`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'CLICK_TICKER',
          extra: { kind: item.kind, target: item.target }
        })
      }).catch(() => {});
    } catch (e) {
      // Ignore tracking errors
    }
  }, []);
  
  // Handle item click
  const handleItemClick = useCallback((e, item) => {
    e.preventDefault();
    e.stopPropagation();
    
    console.log('[Ticker] Item clicked:', item.target);
    trackClick(item);
    
    if (!item.target || typeof item.target !== 'string') {
      console.log('[Ticker] No target, returning');
      return;
    }
    
    try {
      const targetUrl = new URL(item.target, window.location.origin);
      const params = targetUrl.searchParams;
      const category = params.get('category');
      const search = params.get('search');
      
      console.log('[Ticker] Parsed:', { category, search });
      
      if (category || search) {
        // Build query string
        const queryString = new URLSearchParams();
        if (category) queryString.set('category', category);
        if (search) queryString.set('search', search);
        
        const path = `/?${queryString.toString()}`;
        console.log('[Ticker] Navigating to:', path);
        
        // Use navigate directly (not ref)
        navigate(path);
      } else {
        const safePath = item.target.startsWith('/') ? item.target : `/${item.target}`;
        console.log('[Ticker] Navigating to path:', safePath);
        navigate(safePath);
      }
    } catch (err) {
      console.error('[Ticker] Error:', err);
      const safePath = item.target.startsWith('/') ? item.target : `/${item.target}`;
      navigate(safePath);
    }
  }, [trackClick, navigate]);
  
  // Measure widths & setup animation when items/RTL change
  useEffect(() => {
    const viewport = viewportRef.current;
    const track = trackRef.current;
    const content = contentRef.current;
    if (!viewport || !track || !content) return;
    
    const measure = () => {
      const w = content.getBoundingClientRect().width;
      contentWRef.current = Math.max(1, Math.floor(w));
      
      // Start position: begin visible immediately
      // For LTR: start at 0, move left (negative)
      // For RTL: start at 0, move right (positive)
      xRef.current = 0;
      lastTsRef.current = 0;
      
      // Apply transform immediately
      track.style.transform = `translate3d(${xRef.current}px, 0, 0)`;
    };
    
    // Initial measure
    measure();
    
    // Re-measure on resize
    const ro = new ResizeObserver(() => {
      measure();
    });
    ro.observe(viewport);
    ro.observe(content);
    
    return () => ro.disconnect();
  }, [isRTL, displayItems.length]);
  
  // Animation loop using requestAnimationFrame (no React updates)
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    
    const step = (ts) => {
      if (!lastTsRef.current) lastTsRef.current = ts;
      const dtMs = ts - lastTsRef.current;
      lastTsRef.current = ts;
      
      if (!pausedRef.current) {
        const dt = dtMs / 1000;
        const dx = SPEED_PX_PER_SEC * dt;
        const contentW = contentWRef.current || 1;
        
        // LTR: move left (negative), wrap when <= -contentW
        // RTL: move right (positive), wrap when >= contentW
        if (!isRTL) {
          xRef.current -= dx;
          if (xRef.current <= -contentW) {
            xRef.current += contentW;
          }
        } else {
          xRef.current += dx;
          if (xRef.current >= contentW) {
            xRef.current -= contentW;
          }
        }
        
        track.style.transform = `translate3d(${xRef.current}px, 0, 0)`;
      }
      
      rafRef.current = requestAnimationFrame(step);
    };
    
    rafRef.current = requestAnimationFrame(step);
    
    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [isRTL]);
  
  // Pause on hover/touch
  const handlePointerEnter = () => { pausedRef.current = true; };
  const handlePointerLeave = () => { pausedRef.current = false; };
  
  return (
    <div 
      className="home-ticker" 
      data-testid="home-ticker"
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      <div className="ticker-icon-wrapper">
        <TrendingUp size={14} />
      </div>
      
      <div 
        ref={viewportRef}
        className="ticker-track"
        onPointerEnter={handlePointerEnter}
        onPointerLeave={handlePointerLeave}
        onTouchStart={handlePointerEnter}
        onTouchEnd={handlePointerLeave}
      >
        {/* Track: contains TWO copies for seamless loop */}
        <div 
          ref={trackRef} 
          className="ticker-content-js"
        >
          {/* Copy A (measured) */}
          <div ref={contentRef} className="ticker-copy">
            {displayItems.map((item, index) => (
              <button
                key={`a-${item.kind}-${index}`}
                type="button"
                className="ticker-item"
                onClick={(e) => handleItemClick(e, item)}
                onMouseDown={(e) => e.stopPropagation()}
                style={{ pointerEvents: 'auto' }}
                data-testid={`ticker-item-${index}`}
                aria-label={isRTL ? item.labelAr : item.labelEn}
              >
                <span className="ticker-text">
                  {isRTL ? item.labelAr : item.labelEn}
                </span>
                <span className="ticker-separator">•</span>
              </button>
            ))}
          </div>
          
          {/* Copy B (duplicate for seamless loop) */}
          <div className="ticker-copy">
            {displayItems.map((item, index) => (
              <button
                key={`b-${item.kind}-${index}`}
                type="button"
                className="ticker-item"
                onClick={(e) => handleItemClick(e, item)}
                onMouseDown={(e) => e.stopPropagation()}
                style={{ pointerEvents: 'auto' }}
                aria-label={isRTL ? item.labelAr : item.labelEn}
              >
                <span className="ticker-text">
                  {isRTL ? item.labelAr : item.labelEn}
                </span>
                <span className="ticker-separator">•</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default HomeTicker;
